// Pokémon GO search-string builder.
//
// Verified syntax against the in-game searching and filtering reference
// (community-documented). All operators below mirror what the live game
// accepts in the search bar.
//
//   - Comma `,`        → OR    (e.g. multiple dex numbers)
//   - Ampersand `&`    → AND   (additional filters chained on)
//   - `!` prefix       → NOT   (exclude)
//   - `#tagname`       → custom user-applied tag
//   - bare token       → must-have (e.g. `&xxl` means "is XXL")
//
// Valid exclusion keywords: !shiny !lucky !shadow !purified !favorite !traded
// !costume !background !mythical !legendary !4* !xxl !xxs !megaevolve !gigantamax
// !dynamax !special

export type TriState = "exclude" | "include" | "only";

// Filter definition, one row in the easy-mode toggle grid.
export interface FilterDef {
  key: string;            // operator without ! e.g. "shiny", "4*", "xxl"
  label: string;          // shown to user
  allowOnly: boolean;     // some filters are toggleable to "Only" too
  group: "trade-safety" | "rarity" | "size" | "form";
}

export const FILTERS: FilterDef[] = [
  { key: "shiny",       label: "Shiny",        allowOnly: true,  group: "rarity" },
  { key: "lucky",       label: "Lucky",        allowOnly: true,  group: "rarity" },
  { key: "shadow",      label: "Shadow",       allowOnly: true,  group: "form" },
  { key: "purified",    label: "Purified",     allowOnly: true,  group: "form" },
  { key: "favorite",    label: "Favorites",    allowOnly: false, group: "trade-safety" },
  { key: "traded",      label: "Traded",       allowOnly: false, group: "trade-safety" },
  { key: "costume",     label: "Costume",      allowOnly: true,  group: "form" },
  { key: "background",  label: "Background",   allowOnly: true,  group: "form" },
  { key: "mythical",    label: "Mythical",     allowOnly: true,  group: "rarity" },
  { key: "legendary",   label: "Legendary",    allowOnly: true,  group: "rarity" },
  { key: "ultrabeast",  label: "Ultra Beast",  allowOnly: true,  group: "rarity" },
  { key: "4*",          label: "4★ (Perfect)", allowOnly: true,  group: "rarity" },
  { key: "xxl",         label: "XXL",          allowOnly: true,  group: "size" },
  { key: "xxs",         label: "XXS",          allowOnly: true,  group: "size" },
  { key: "megaevolve",  label: "Mega",         allowOnly: true,  group: "form" },
  { key: "gigantamax",  label: "Gigantamax",   allowOnly: true,  group: "form" },
  { key: "dynamax",     label: "Dynamax",      allowOnly: true,  group: "form" },
];

export interface TagFilter {
  tag: string;          // includes leading "#", normalized
  state: TriState;
}

export interface RawFilter {
  expr: string;         // raw operator token, e.g. "cp-1500", "@water", "evolve"
  // 3-state: "off" contributes no token (dormant), "exclude" -> !expr,
  // "include" -> expr. "off" lets the permanent built-in operators sit in the
  // list without constraining every search until the user flips one.
  state: "off" | "exclude" | "include";
  permanent?: boolean;  // built-in operator that is always present + non-removable
}

export type FilterStateMap = Record<string, TriState>;

export interface TradeStringConfig {
  source:
    | "main" | "shiny" | "lucky" | "perfect" | "xxl" | "xxs"
    | "purified" | "legendary" | "transfer";
  dexNumbers: number[];          // current effective list (auto or custom)
  dexMode: "auto" | "custom";
  filters: FilterStateMap;       // keyed by FilterDef.key
  tags: TagFilter[];
  rawFilters: RawFilter[];
}

// ─── Built-in presets ───
export const PRESETS: { id: string; label: string; description: string; build: (current: TradeStringConfig) => TradeStringConfig }[] = [
  {
    id: "safe-trade",
    label: "Safe to trade",
    description: "Exclude shinies, luckies, perfects, favorites, traded, and your custom tags.",
    build: (c) => ({
      ...c,
      filters: {
        shiny: "exclude", lucky: "exclude", shadow: "exclude", purified: "exclude",
        favorite: "exclude", traded: "exclude", costume: "exclude", background: "exclude",
        mythical: "exclude", legendary: "include", "4*": "exclude",
        xxl: "include", xxs: "include", megaevolve: "include",
        gigantamax: "include", dynamax: "include",
      },
      tags: c.tags.map((t) => ({ ...t, state: "exclude" })),
    }),
  },
  {
    id: "open-trade",
    label: "Open trade",
    description: "Almost everything. Only excludes favorites and previously-traded.",
    build: (c) => ({
      ...c,
      filters: Object.fromEntries(FILTERS.map((f) => [f.key,
        (f.key === "favorite" || f.key === "traded") ? "exclude" as TriState : "include" as TriState
      ])) as FilterStateMap,
      tags: c.tags.map((t) => ({ ...t, state: "include" })),
    }),
  },
  {
    id: "lucky-friends",
    label: "Lucky friends only",
    description: "Pokémon that ARE lucky, no shinies, no perfects, no favorites.",
    build: (c) => ({
      ...c,
      filters: {
        shiny: "exclude", lucky: "only", shadow: "include", purified: "include",
        favorite: "exclude", traded: "exclude", costume: "include", background: "include",
        mythical: "include", legendary: "include", "4*": "exclude",
        xxl: "include", xxs: "include", megaevolve: "include",
        gigantamax: "include", dynamax: "include",
      },
      tags: c.tags.map((t) => ({ ...t, state: "exclude" })),
    }),
  },
  {
    id: "xxl-hunt",
    label: "XXL hunting",
    description: "Only XXL Pokémon, exclude favorites and traded.",
    build: (c) => ({
      ...c,
      filters: {
        shiny: "include", lucky: "include", shadow: "include", purified: "include",
        favorite: "exclude", traded: "exclude", costume: "include", background: "include",
        mythical: "include", legendary: "include", "4*": "include",
        xxl: "only", xxs: "exclude", megaevolve: "include",
        gigantamax: "include", dynamax: "include",
      },
      tags: c.tags,
    }),
  },
];

export const DEFAULT_TAGS: TagFilter[] = [
  { tag: "#trade",  state: "exclude" },
  { tag: "#keep",   state: "exclude" },
  { tag: "#pvp",    state: "exclude" },
];

// ─── Permanent built-in custom operators ───
// A fixed palette of common in-game search operators that is always present
// under "Custom filters". They are non-removable and default to "off" so they
// stay dormant until the user flips one to Exclude / Include — otherwise every
// search would carry all of them at once.
export const BUILTIN_RAW_PRESETS: string[] = [
  "cp-1500",
  "cp-2500",
  // "distance100-" matches everything caught 100km+ away (dash after the number
  // is "greater than"), i.e. XL-candy-on-trade mons. "distance1000-" is 1000km+.
  "distance100-",
  "distance1000-",
  // "year-2024" matches everything caught in 2024 OR earlier — PoGo players
  // hold aged mons for lucky trades (older = higher lucky odds). "year2026"
  // (no dash) matches only mons caught in 2026.
  "year-2024",
  "year2026",
  "age0",
  "age1-7",
  // Origin operators: mons that hatched from an egg, and mons caught in raids.
  "hatched",
  "raid",
];

/** The permanent operators in their default (dormant) state. */
export function defaultRawFilters(): RawFilter[] {
  return BUILTIN_RAW_PRESETS.map((expr) => ({ expr, state: "off", permanent: true }));
}

/**
 * Ensure every permanent built-in operator is present (dormant unless the user
 * previously set a state for it), followed by any user-added operators. Idempotent
 * — safe to run on a fresh config, a legacy config, or a loaded preset.
 */
export function mergeBuiltinRawFilters(existing: RawFilter[]): RawFilter[] {
  const prevByExpr = new Map(existing.map((r) => [r.expr.toLowerCase(), r]));
  const builtins: RawFilter[] = BUILTIN_RAW_PRESETS.map((expr) => {
    const prev = prevByExpr.get(expr.toLowerCase());
    return { expr, state: prev ? prev.state : "off", permanent: true };
  });
  const builtinSet = new Set(BUILTIN_RAW_PRESETS.map((e) => e.toLowerCase()));
  const userAdded = existing
    .filter((r) => !builtinSet.has(r.expr.toLowerCase()))
    .map((r) => ({ expr: r.expr, state: r.state, permanent: false }));
  return [...builtins, ...userAdded];
}

// ─── Presentation metadata for the preset explainer ───
// An emoji + short human label per filter key, used to render the visual
// "logic pills" under each preset (e.g. a crossed-out ✨ Shiny for exclude).
// Kept here next to FILTERS so the two lists can't drift apart.
export const FILTER_META: Record<string, { emoji: string; short: string }> = {
  shiny:      { emoji: "✨", short: "Shiny" },
  lucky:      { emoji: "🍀", short: "Lucky" },
  shadow:     { emoji: "🌑", short: "Shadow" },
  purified:   { emoji: "⚪", short: "Purified" },
  favorite:   { emoji: "⭐", short: "Favorites" },
  traded:     { emoji: "🔄", short: "Traded" },
  costume:    { emoji: "🎩", short: "Costume" },
  background: { emoji: "🖼️", short: "Background" },
  mythical:   { emoji: "🔮", short: "Mythical" },
  legendary:  { emoji: "👑", short: "Legendary" },
  ultrabeast: { emoji: "👾", short: "Ultra Beast" },
  "4*":       { emoji: "💯", short: "Perfect (4★)" },
  xxl:        { emoji: "📏", short: "XXL" },
  xxs:        { emoji: "🤏", short: "XXS" },
  megaevolve: { emoji: "💥", short: "Mega" },
  gigantamax: { emoji: "🔴", short: "Gigantamax" },
  dynamax:    { emoji: "🟣", short: "Dynamax" },
};

export interface PresetEffect {
  key: string;
  emoji: string;
  short: string;
  state: TriState;   // "exclude" | "only" (we only surface the meaningful ones)
}

// Derive the human-readable effects of a preset directly from what its build()
// actually produces — so the visuals can never lie about the logic. We surface
// only the filters the preset acts on (exclude / only); "include" is the
// no-op default and would just be noise. Returns exclude pills first, then
// only pills, each in FILTERS declaration order.
export function presetEffects(presetId: string): PresetEffect[] {
  const preset = PRESETS.find((p) => p.id === presetId);
  if (!preset) return [];
  const empty: TradeStringConfig = {
    source: "lucky", dexNumbers: [], dexMode: "auto",
    filters: {}, tags: [], rawFilters: [],
  };
  const built = preset.build(empty);
  const effects: PresetEffect[] = [];
  for (const f of FILTERS) {
    const state = built.filters[f.key];
    if (state === "exclude" || state === "only") {
      const meta = FILTER_META[f.key] ?? { emoji: "•", short: f.label };
      effects.push({ key: f.key, emoji: meta.emoji, short: meta.short, state });
    }
  }
  // exclude pills first, then only pills
  return effects.sort((a, b) => {
    if (a.state === b.state) return 0;
    return a.state === "exclude" ? -1 : 1;
  });
}

export function defaultConfig(
  dexNumbers: number[],
  source: TradeStringConfig["source"] = "lucky",
): TradeStringConfig {
  const base: TradeStringConfig = {
    source,
    dexNumbers,
    dexMode: "auto",
    filters: {},
    tags: DEFAULT_TAGS,
    rawFilters: defaultRawFilters(),
  };
  return PRESETS[0].build(base);
}

// ─── String builder ───
export interface BuildResult {
  string: string;
  excludeCount: number;
  onlyCount: number;
  includeCount: number;
  dexCount: number;
}

export function buildString(c: TradeStringConfig): BuildResult {
  const tokens: string[] = [];

  // 1) Easy-mode filters
  const filterTokens: string[] = [];
  for (const f of FILTERS) {
    const state = c.filters[f.key];
    if (state === "exclude") filterTokens.push(`!${f.key}`);
    else if (state === "only") filterTokens.push(f.key);
    // include = no token
  }

  // 2) Custom tags
  const tagTokens: string[] = [];
  for (const t of c.tags) {
    const tagName = t.tag.startsWith("#") ? t.tag : `#${t.tag}`;
    if (t.state === "exclude") tagTokens.push(`!${tagName}`);
    else if (t.state === "only") tagTokens.push(tagName);
  }

  // 3) Custom raw
  const rawTokens: string[] = [];
  for (const r of c.rawFilters) {
    if (!r.expr.trim()) continue;
    if (r.state === "off") continue; // dormant: contributes no token
    rawTokens.push(r.state === "exclude" ? `!${r.expr.trim()}` : r.expr.trim());
  }

  // Compose: dex numbers (comma-separated, OR group) + AND chain of filters
  const dexPart = c.dexNumbers.length > 0 ? c.dexNumbers.join(",") : "";
  const allFilters = [...filterTokens, ...tagTokens, ...rawTokens];
  let str = dexPart;
  if (allFilters.length > 0) {
    str = (dexPart ? dexPart : "") + (dexPart ? "&" : "") + allFilters.join("&");
  }

  // Counts
  let excludeCount = 0, onlyCount = 0, includeCount = 0;
  for (const f of FILTERS) {
    const s = c.filters[f.key];
    if (s === "exclude") excludeCount++;
    else if (s === "only") onlyCount++;
    else if (s === "include") includeCount++;
  }
  for (const t of c.tags) {
    if (t.state === "exclude") excludeCount++;
    else if (t.state === "only") onlyCount++;
    else includeCount++;
  }
  for (const r of c.rawFilters) {
    if (r.state === "exclude") excludeCount++;
    else if (r.state === "include") includeCount++;
    // "off" contributes nothing
  }

  return {
    string: str,
    excludeCount,
    onlyCount,
    includeCount,
    dexCount: c.dexNumbers.length,
  };
}

// Detect whether the config exactly matches a built-in preset
export function detectActivePreset(c: TradeStringConfig): string | null {
  for (const p of PRESETS) {
    const built = p.build(c);
    if (
      JSON.stringify(built.filters) === JSON.stringify(c.filters) &&
      JSON.stringify(built.tags.map((t) => t.state)) === JSON.stringify(c.tags.map((t) => t.state))
    ) {
      return p.id;
    }
  }
  return null;
}
